import { Card, FormField, Input } from '@playerbase/ui';

import {
  type BuilderMetadata,
  type UseFormBuilderState,
} from '../../../games/use-form-builder-state.js';

interface MetadataCardProps {
  metadata: BuilderMetadata;
  builder: UseFormBuilderState;
}

const FIELD_LABEL_CLASS =
  'font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted';

// Form details split into portal-only and Discord-facing groups so the
// studio sees which copy lands where. The Discord title is capped at
// 45 to match the modal's hard Label cap; portal name has more room.
export function MetadataCard({ metadata, builder }: MetadataCardProps): JSX.Element {
  return (
    <Card>
      <Card.Header>
        <Card.Title>Form details</Card.Title>
        <Card.Description>
          Two sets of copy: one for your dashboard, one for Discord.
        </Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-6">
        <section className="flex flex-col gap-4">
          <header className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-text">Portal</h3>
            <p className="text-xs text-text-muted">
              You and your team see these. Players never do.
            </p>
          </header>

          <FormField>
            <FormField.Label className={FIELD_LABEL_CLASS} data-required>
              Form name
            </FormField.Label>
            <FormField.Control>
              <Input
                value={metadata.name}
                onChange={(e) => builder.updateMetadata({ name: e.target.value })}
                maxLength={80}
                placeholder="Beta playtest feedback"
              />
            </FormField.Control>
            <FormField.HelpText>Up to 80 characters.</FormField.HelpText>
          </FormField>

          <FormField>
            <FormField.Label className={FIELD_LABEL_CLASS}>
              Internal notes
            </FormField.Label>
            <FormField.Control>
              <Input
                value={metadata.internalDescription}
                onChange={(e) =>
                  builder.updateMetadata({ internalDescription: e.target.value })
                }
                placeholder="Optional. e.g. 'Mid-build 0.4 wave 2 questionnaire'."
              />
            </FormField.Control>
          </FormField>
        </section>

        <section className="flex flex-col gap-4">
          <header className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-text">Discord</h3>
            <p className="text-xs text-text-muted">
              Players see these in Discord.
            </p>
          </header>

          <FormField>
            <FormField.Label className={FIELD_LABEL_CLASS} data-required>
              Discord title
            </FormField.Label>
            <FormField.Control>
              <Input
                value={metadata.title}
                onChange={(e) => builder.updateMetadata({ title: e.target.value })}
                maxLength={45}
                placeholder="Beta playtest feedback"
              />
            </FormField.Control>
            <FormField.HelpText>
              Up to 45 characters (Discord modal title limit).
            </FormField.HelpText>
          </FormField>

          <FormField>
            <FormField.Label className={FIELD_LABEL_CLASS}>
              Discord description
            </FormField.Label>
            <FormField.Control>
              <Input
                value={metadata.discordDescription}
                onChange={(e) =>
                  builder.updateMetadata({ discordDescription: e.target.value })
                }
                placeholder="Optional. Shown beneath the form button in Discord."
              />
            </FormField.Control>
          </FormField>
        </section>
      </Card.Content>
    </Card>
  );
}
